import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, PackagePlus, X, ImagePlus } from "lucide-react";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/image";
import T from "@/lib/testIds";

export default function RequestForm({ open, onOpenChange }) {
  const [nama, setNama] = useState("");
  const [kontak, setKontak] = useState("");
  const [barang, setBarang] = useState("");
  const [catatan, setCatatan] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [fotoUrls, setFotoUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const renderedAt = useRef(0);

  useEffect(() => {
    if (open) {
      renderedAt.current = Date.now();
      // reset preview on open
      setFotoUrls([]);
    }
  }, [open]);

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (fotoUrls.length + files.length > 5) {
      toast.error("Maksimal 5 foto");
      return;
    }
    setUploading(true);
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name}: >5MB, dilewati`); continue; }
      try {
        const compressed = await compressImage(f, { maxDim: 1400, quality: 0.82 });
        const fd = new FormData();
        fd.append("file", compressed);
        const r = await api.post("/upload/public", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFotoUrls((prev) => [...prev, r.data.secure_url]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload gagal");
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removePhoto = (idx) => setFotoUrls((arr) => arr.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!nama.trim() || !kontak.trim() || !barang.trim()) {
      toast.error("Nama, kontak, dan barang wajib diisi");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/requests", {
        nama, kontak, barang, catatan,
        foto_urls: fotoUrls,
        website,
        form_rendered_at: renderedAt.current,
      });
      toast.success("Request terkirim. Admin akan follow up ke kontak kamu.");
      setNama(""); setKontak(""); setBarang(""); setCatatan(""); setFotoUrls([]);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal kirim request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-[2rem] max-h-[92vh] overflow-y-auto glass border-t border-plum/10 shadow-dreamy safe-bottom">
        <SheetHeader>
          <SheetTitle className="font-serif italic text-3xl flex items-center gap-2 text-plum dark:text-cream">
            <PackagePlus className="h-5 w-5 text-babyblue-600" strokeWidth={2} /> Titip barang
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground tracking-wide">
            Barang belum ada di katalog? Titip di sini, kami cariin di Bangkok.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} data-testid={T.requestForm} className="mt-6 space-y-4">
          {/* honeypot */}
          <div style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }} aria-hidden="true">
            <label>Website</label>
            <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block text-plum/70 dark:text-cream/70">Nama</label>
            <Input data-testid={T.requestName} value={nama} onChange={(e) => setNama(e.target.value)} placeholder="Nama panggilan" className="h-11 rounded-2xl border border-plum/15" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block text-plum/70 dark:text-cream/70">Kontak (WA / IG)</label>
            <Input data-testid={T.requestKontak} value={kontak} onChange={(e) => setKontak(e.target.value)} placeholder="08xx / @ig" className="h-11 rounded-2xl border border-plum/15" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block text-plum/70 dark:text-cream/70">Barang yang dicari</label>
            <Input data-testid={T.requestBarang} value={barang} onChange={(e) => setBarang(e.target.value)} placeholder="cth: Cathy Doll BB Cream shade 21" className="h-11 rounded-2xl border border-plum/15" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block text-plum/70 dark:text-cream/70">Catatan (opsional)</label>
            <Textarea data-testid={T.requestCatatan} value={catatan} onChange={(e) => setCatatan(e.target.value)} rows={3} placeholder="ukuran / warna / budget..." className="rounded-2xl border border-plum/15" />
          </div>

          {/* Photo attach */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block text-plum/70 dark:text-cream/70">Foto referensi (opsional, maks 5)</label>
            <div className="flex flex-wrap gap-2.5">
              {fotoUrls.map((u, i) => (
                <div key={i} className="relative h-20 w-20 rounded-2xl overflow-hidden border border-plum/15 shadow-dreamy">
                  <img src={u} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    aria-label="Remove photo"
                    className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-plum/85 text-white active:scale-90 transition-transform"
                  >
                    <X className="h-3 w-3" strokeWidth={2.5} />
                  </button>
                </div>
              ))}
              {fotoUrls.length < 5 && (
                <label
                  data-testid="request-photo-upload"
                  className={`h-20 w-20 rounded-2xl border border-dashed border-plum/25 grid place-items-center cursor-pointer bg-white/40 dark:bg-plum/20 hover:border-sakura-500 hover:text-sakura-600 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                >
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onFileChange} disabled={uploading} />
                  <div className="text-center">
                    <ImagePlus className="h-4 w-4 mx-auto text-plum/60 dark:text-cream/60" strokeWidth={1.8} />
                    <span className="text-[9px] font-medium uppercase tracking-wider text-plum/60 dark:text-cream/60 mt-1 block">{uploading ? "Upload..." : "Tambah"}</span>
                  </div>
                </label>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">Bantu admin gambaran barangnya (screenshot IG / marketplace). Maks 5MB per foto.</p>
          </div>

          <Button
            type="submit"
            data-testid={T.requestSubmit}
            disabled={submitting || uploading}
            className="w-full h-12 rounded-full font-semibold text-xs uppercase tracking-[0.2em] bg-babyblue-400 hover:bg-babyblue-500 text-plum border border-plum/10 shadow-[0_6px_20px_rgba(180,213,245,0.5)] active:scale-95 transition-transform"
          >
            <Send className="h-4 w-4 mr-2" strokeWidth={2} /> {submitting ? "Mengirim..." : "Kirim Request"}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
